import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface PlatformStats {
  id: number;
  total_licenses: number;
  active_licenses: number;
  expired_licenses: number;
  suspended_licenses: number;
  revoked_licenses: number;
  pending_applications: number;
  approved_applications: number;
  rejected_applications: number;
  total_customers: number;
  online_seats: number;
  issued_this_month?: number;
  expiring_soon?: number;
  pending_customers?: number;
  updated_at: string;
}

export interface SystemMetric {
  id: number;
  metric_date: string;
  activations: number;
  licenses_issued: number;
  api_requests: number;
  uptime: number;
}

export interface ActivityLog {
  id: number;
  actor_name: string | null;
  action: string;
  detail: string | null;
  level: string;
  created_at: string;
}

interface HomeData {
  stats: PlatformStats | null;
  metrics: SystemMetric[];
  activities: ActivityLog[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useHomeData(): HomeData {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [metrics, setMetrics] = useState<SystemMetric[]>([]);
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, metricsRes, activityRes] = await Promise.all([
        supabase.from("platform_stats").select("*").eq("id", 1).maybeSingle(),
        supabase.from("system_metrics").select("*").order("metric_date", { ascending: true }).limit(30),
        supabase.from("activity_logs").select("*").order("created_at", { ascending: false }).limit(30),
      ]);

      if (statsRes.error) throw statsRes.error;
      if (metricsRes.error) throw metricsRes.error;
      if (activityRes.error) throw activityRes.error;

      const baseStats = (statsRes.data as PlatformStats | null) ?? null;

      let issuedThisMonth = 0;
      let expiringSoon = 0;
      let pendingCustomers = 0;
      try {
        const { data } = await supabase.functions.invoke("license-stats", { body: {} });
        const parsed = data as {
          issuedThisMonth?: number;
          expiringSoon?: number;
          pendingCustomers?: number;
        } | null;
        issuedThisMonth = parsed?.issuedThisMonth ?? 0;
        expiringSoon = parsed?.expiringSoon ?? 0;
        pendingCustomers = parsed?.pendingCustomers ?? 0;
      } catch {
        // supplementary stats are optional; keep defaults on failure
      }

      setStats(
        baseStats
          ? {
              ...baseStats,
              issued_this_month: issuedThisMonth,
              expiring_soon: expiringSoon,
              pending_customers: pendingCustomers,
            }
          : null,
      );
      setMetrics((metricsRes.data as SystemMetric[] | null) ?? []);
      setActivities((activityRes.data as ActivityLog[] | null) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "数据加载失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { stats, metrics, activities, loading, error, reload: load };
}